import { useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { ApiKey, ApiKeyCreated, Member, PageApiKey, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { managedBots } from "../lib/agents.ts";
import type { Option } from "../lib/data.ts";
import { addMonths, instantToLocal, localToRfc3339 } from "../lib/datetime.ts";
import { formatInstant, formatRelative } from "../lib/format.ts";
import { defaultKeyName } from "../lib/keyNames.ts";
import { orgApi } from "../lib/paths.ts";
import { nameOf, useSpacePeople } from "../lib/people.ts";
import { bump, useAction, useResource } from "../lib/resource.ts";
import { atLeast } from "../lib/roles.ts";
import { me, type PageProps } from "../lib/state.ts";
import { timeZone } from "../lib/zone.ts";
import { Person } from "../ui/Avatar.tsx";
import { Button, StepActions } from "../ui/Button.tsx";
import { ConfirmButton, Dialog } from "../ui/Dialog.tsx";
import { Field, Form, Input, Select } from "../ui/form.tsx";
import { PlusIcon } from "../ui/icons.tsx";
import { Async, EmptyState } from "../ui/misc.tsx";
import { FORM_ROW } from "../ui/styles.ts";
import { Table, Td, Th } from "../ui/Table.tsx";
import { KeyActivityButton, LastClient } from "./settings/activity.tsx";
import { SettingsNav } from "./settings/nav.tsx";
import { Secret } from "./settings/secret.tsx";

const MONTHS_PER_YEAR = 12;

function KeyRow({
  space,
  k,
  members,
  onChange,
}: {
  space: SpaceOut;
  k: ApiKey;
  members: readonly Member[] | undefined;
  onChange: () => void;
}) {
  const [revoke, busy] = useAction(async () => {
    await api.del(orgApi(space, `/keys/${k.id}`));
    onChange();
  });
  return (
    <tr>
      <Td>
        <span class="font-medium">{k.name}</span>
        <code class="block text-xs text-zinc-500">{k.prefix}…</code>
      </Td>
      <Td label={t("keys.user")}>
        <Person name={nameOf(members, k.user)} id={k.user} />
      </Td>
      <Td label={t("keys.lastUsed")}>
        {k.last_used_at ? formatRelative(k.last_used_at) : t("keys.never")}
        <LastClient c={k.last_client} />
      </Td>
      <Td label={t("keys.expires")}>{k.expires_at ? formatInstant(k.expires_at) : t("keys.noExpiry")}</Td>
      <Td align="right" label="">
        <KeyActivityButton space={space} k={k} />
        <ConfirmButton
          size="sm"
          variant="danger"
          label={t("keys.revoke")}
          title={t("keys.revoke")}
          message={t("keys.revokeMessage", { name: k.name })}
          confirmLabel={t("keys.revoke")}
          busy={busy}
          onConfirm={revoke}
        />
      </Td>
    </tr>
  );
}

type Expiry = "month" | "year" | "never" | "date";

/** Now plus `months`, as a datetime-local value in the viewer's zone. */
const inMonths = (months: number): string => addMonths(instantToLocal(Date.now(), timeZone.value), months);

/** When a key expires (in the viewer's zone): in a month, a year (the default), never, or on a date. */
function ExpiryFields(p: { expiry: Expiry; date: string; onExpiry: (e: Expiry) => void; onDate: (d: string) => void }) {
  const on = (months: number) =>
    formatInstant(localToRfc3339(inMonths(months), timeZone.value) ?? "", { dateStyle: "medium" });
  return (
    <>
      <Field label={t("keys.expires")}>
        <Select
          value={p.expiry}
          onValue={(v) => p.onExpiry(v as Expiry)}
          options={[
            { value: "month", label: t("keys.inMonth", { date: on(1) }) },
            { value: "year", label: t("keys.inYear", { date: on(MONTHS_PER_YEAR) }) },
            { value: "never", label: t("keys.noExpiry") },
            { value: "date", label: t("keys.onDate") },
          ]}
        />
      </Field>
      {p.expiry === "date" ? (
        <Field label={t("keys.expiresOn", { zone: timeZone.value })}>
          <Input type="datetime-local" value={p.date} onValue={p.onDate} required={true} />
        </Field>
      ) : null}
    </>
  );
}

const NEW_AGENT = "new";

/** Who a key acts as (admins pick): agents and integrations, or me. With `agentFirst` agents lead, one is picked by
 *  default, and "New agent…" adds one; otherwise "Me" leads and is the default. */
function actsAsOptions(bots: readonly Member[], agentFirst: boolean): Option[] {
  const agents: Option[] = bots.map((b) => ({ value: b.id, label: b.name, group: t("keys.agents") }));
  const mine: Option = { value: "", label: t("keys.me"), group: t("keys.you") };
  return agentFirst
    ? [...agents, { value: NEW_AGENT, label: t("keys.newAgent"), group: t("keys.agents") }, mine]
    : [mine, ...agents];
}

interface CreateKeyProps {
  space: SpaceOut;
  bots: readonly Member[];
  /** For connecting an agent: act as an agent by default (or a new one), rather than as me. */
  agentFirst?: boolean;
  /** Who it acts as, initially (else the default above). */
  initialUser?: string;
  /** Back (closing the dialog, or a step back): beside "Create key", under the fields. */
  onBack: () => void;
  onCreated: (secret: string) => void;
  /** The client it's for, by name (Claude Code, Cursor…), when one was picked: part of its default name. */
  client?: string | undefined;
  /** For an agent being added (named, not made yet): it acts as that one, made (`make`: its id) as the key is. */
  adding?: { name: string; make: () => Promise<string> };
}

/**
 * New key for yourself or (admins) for an agent/integration, expiring in a year unless picked otherwise. Its name
 * starts as who it acts as (and its client), following that choice until you type one of your own; left empty, it's
 * that default.
 */
export function CreateKey({
  space,
  bots,
  agentFirst = false,
  initialUser,
  onBack,
  onCreated,
  client,
  adding,
}: CreateKeyProps) {
  // Admins pick whom it acts as; so do editors with agents of their own (`bots`: those they may make keys for).
  // Not for one being added: it's that one's.
  const admin = !adding && (atLeast(space.role, "admin") || bots.length > 0);
  // What you typed (null: nothing yet, so the name follows who it acts as).
  const [typed, setTyped] = useState<string | null>(null);
  const [picked, setUser] = useState<string | null>(initialUser ?? null);
  const [agentName, setAgentName] = useState("");
  const [expiry, setExpiry] = useState<Expiry>("year");
  const [date, setDate] = useState(() => inMonths(MONTHS_PER_YEAR));
  const keys = useResource(`keys:${space.org}`, (signal) =>
    api.get<PageApiKey>(orgApi(space, "/keys"), undefined, signal),
  );
  const user = picked ?? (agentFirst && admin ? (bots[0]?.id ?? NEW_AGENT) : "");
  const actor = adding
    ? adding.name
    : user === NEW_AGENT
      ? agentName
      : user === ""
        ? (me.value?.name ?? "")
        : (bots.find((b) => b.id === user)?.name ?? "");
  const fallback = defaultKeyName(actor, client, keys.data?.data.map((k) => k.name) ?? []);
  const name = typed ?? fallback;
  const [create, busy] = useAction(async () => {
    const local = { month: inMonths(1), year: inMonths(MONTHS_PER_YEAR), never: null, date }[expiry];
    const expiresAt = local ? localToRfc3339(local, timeZone.value) : null;
    let as = adding ? await adding.make() : user || null;
    if (!adding && user === NEW_AGENT) {
      const m = await api.post<Member>(orgApi(space, "/members"), { kind: "agent", name: agentName, role: "editor" });
      as = m.id;
      setUser(m.id);
      setAgentName("");
      bump();
    }
    const keyName = name.trim() || fallback;
    const key = await api.post<ApiKeyCreated>(orgApi(space, "/keys"), {
      name: keyName,
      user: as,
      expires_at: expiresAt,
    });
    setTyped(null);
    bump();
    onCreated(key.secret);
  });
  return (
    <Form onSubmit={create}>
      <div class={FORM_ROW}>
        <div class="min-w-56 flex-1">
          <Field label={t("keys.keyName")}>
            <Input value={name} onValue={setTyped} placeholder={fallback} required={fallback === ""} />
          </Field>
        </div>
        {admin ? (
          <Field label={t("keys.user")}>
            <Select value={user} onValue={setUser} options={actsAsOptions(bots, agentFirst)} />
          </Field>
        ) : null}
        {user === NEW_AGENT ? (
          <Field label={t("keys.agentName")}>
            <Input value={agentName} onValue={setAgentName} required={true} />
          </Field>
        ) : null}
        <ExpiryFields expiry={expiry} date={date} onExpiry={setExpiry} onDate={setDate} />
      </div>
      <StepActions onBack={onBack}>
        <Button answer="confirm" submit={true} busy={busy} variant="primary">
          {t("keys.create")}
        </Button>
      </StepActions>
    </Form>
  );
}

export function ApiKeys({ space }: PageProps) {
  const keys = useResource(`keys:${space.org}`, (signal) =>
    api.get<PageApiKey>(orgApi(space, "/keys"), undefined, signal),
  );
  const people = useSpacePeople(space);
  const [secret, setSecret] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  return (
    <>
      <SettingsNav space={space} tab="/keys" />
      <div class="mb-4 flex">
        <Button variant="primary" onClick={() => setCreating(true)}>
          <PlusIcon />
          {t("keys.new")}
        </Button>
      </div>
      <Async r={keys}>
        {(page) =>
          page.data.length === 0 ? (
            <EmptyState title={t("keys.empty")} />
          ) : (
            <Table stack={true}>
              <thead class="bg-zinc-50">
                <tr>
                  <Th>{t("keys.name")}</Th>
                  <Th>{t("keys.user")}</Th>
                  <Th>{t("keys.lastUsed")}</Th>
                  <Th>{t("keys.expires")}</Th>
                  <Th />
                </tr>
              </thead>
              <tbody class="divide-y divide-zinc-100">
                {page.data.map((k) => (
                  <KeyRow key={k.id} space={space} k={k} members={people} onChange={keys.reload} />
                ))}
              </tbody>
            </Table>
          )
        }
      </Async>
      <Dialog open={creating} onClose={() => setCreating(false)} title={t("keys.create")}>
        <CreateKey
          space={space}
          bots={managedBots(people, me.value?.id, space.role)}
          onBack={() => setCreating(false)}
          onCreated={(s) => {
            setCreating(false);
            setSecret(s);
            keys.reload();
          }}
        />
      </Dialog>
      <Secret secret={secret} onClose={() => setSecret(null)} />
    </>
  );
}
